// Bài hướng dẫn trang Khiếu nại — /complaints, chia theo HAI THẺ.
//
// 25/09/2026 màn Tiếng khách gộp vào đây thành thẻ đầu, và thẻ "Khiếu nại" của
// trang CSKH cũ bị bỏ. Bài này vì thế gom ba bài cũ (`complaints`,
// `customer_voice`, `cskh_complaint`) về một chỗ.
//
// ⚠️ Ranh giới thông tin ở `../types.ts` áp NGUYÊN VẸN. Trang này dễ vượt vạch ở
// mấy chỗ, nên cố ý KHÔNG viết:
//   · mốc 30 phút là mốc của quy chế nào, và ai bị ghi gì khi quá mốc;
//   · vì sao có vụ phải lập phiếu và vụ nào thì không — đó là cơ chế kiểm soát,
//     biết chỗ hở là biết chỗ không ai canh;
//   · mốc tiền phải xin duyệt khi hoàn tiền;
//   · kết luận "lỗi của ai" dẫn tới điều gì cho cơ sở hay cho người bị ghi lỗi,
//     và phiếu trễ hạn bị tính vào đâu (QĐ 0326) — bài chỉ nói ba mốc hạn là gì
//     và nhãn trên màn nghĩa là gì, vì đó là chữ người đọc nhìn thấy.
// Người đọc chỉ cần biết: khách nói thì ghi, ghi rồi phải trả lời khách, trả
// lời rồi mới quyết xử thế nào.

import { COMPLAINTS_TAB_LABEL } from "@/features/complaints/labels";
import type { GuideBook } from "../types";

export const COMPLAINTS_GUIDES: GuideBook = {
  complaints: {
    purpose:
      "Một cửa cho mọi vấn đề của khách: ghi lại lời khách, và lập phiếu cho vụ nào phải làm lại hoặc bù cho khách.",
    where: [
      'Đầu trang: hai thẻ "Tiếng khách" và "Phiếu khiếu nại" — bấm để đổi qua lại.',
      '"Tiếng khách" là sổ ghi mọi lời khách, kể cả vụ không gắn đơn nào.',
      '"Phiếu khiếu nại" là những vụ đã chuyển thành việc phải bù cho khách.',
      'Nút "Hướng dẫn" ở góc trên phải mở đúng bài của thẻ bạn đang đứng.',
    ],
    askWho:
      "Quản lý trực tiếp của bạn; khách đang ở tiệm thì hỏi người phụ trách cơ sở.",
    tabs: [
      {
        key: "voice",
        label: COMPLAINTS_TAB_LABEL.voice,
        purpose:
          "Sổ ghi mọi vấn đề khách nói ra — dù khách gọi điện, nói tại quầy, bấm trên cổng, hay để lại nhận xét.",
        where: [
          'Đầu thẻ: hai ô "Từ ngày" và "Đến ngày" chọn khoảng thời gian muốn xem.',
          'Ngay dưới là ba thẻ con "Chờ phản hồi", "Đang xử lý", "Đã xong" — mỗi thẻ có con số đếm bên cạnh.',
          'Mỗi dòng trong bảng: kênh khách nói (kèm mức nặng nhẹ), tên khách và mã đơn, nguyên văn lời khách, cột "Phản hồi sau", trạng thái, và các nút việc cần làm.',
          'Nút "Ghi tiếng khách" nằm ở mép phải, ngang hàng với ba thẻ con.',
          'Dòng khách gửi từ nút "Tôi đang rất bực" trên cổng có thêm chữ "Đã hứa gọi lại trước …" ngay dưới cột "Phản hồi sau".',
          'Cột "Nơi" cho biết vụ xảy ra ở cơ sở nào; ghi "cả vùng" khi chỉ biết miền, ghi "Chưa rõ nơi" khi chưa biết.',
          'Các nút trên dòng: "Đã phản hồi khách", "Nhận việc", "Gắn đơn", "Ghi kết quả", "Lập phiếu khiếu nại".',
          'Nút "Gắn đơn" chỉ hiện trên dòng chưa có đơn.',
          "Nút nào đang khoá thì có một dòng chữ nhỏ ngay bên dưới nói vì sao khoá.",
          "Trên điện thoại, mỗi dòng hiện thành một thẻ dọc thay vì bảng ngang.",
        ],
        howItWorks: [
          "Một số dòng do máy tự ghi vào (ví dụ khi có người mở phiếu khiếu nại), số còn lại do người bấm nút ghi tay.",
          "Thẻ này không tự đổi trạng thái của dòng nào: dòng chỉ đổi khi có người bấm.",
          'Khách bấm "Tôi đang rất bực" trên cổng thì máy tự ghi một dòng ở Mức Nặng, kèm giờ đã hứa sẽ gọi lại cho khách.',
          'Dòng có giờ hứa gọi lại: người đang theo vụ nhận một tin nhắc khi sắp tới hẹn, và một tin nữa nếu đã quá hẹn mà chưa ai bấm "Đã phản hồi khách".',
          "Khách bổ sung thêm trên cổng thì dòng cũ tự cập nhật lời khách và giờ hẹn, không đẻ thêm dòng mới.",
          'Cột "Phản hồi sau" đếm từ lúc khách nói tới lúc có người bấm "Đã phản hồi khách". Chưa bấm thì nó vẫn đang chạy.',
          'Bấm "Đã phản hồi khách" chỉ ghi được MỘT LẦN — bấm lại không đổi gì, không phải lỗi.',
          'Dòng đã gắn với một phiếu khiếu nại (kể cả phiếu máy tự mở khi khách chấm 1–2 sao) thì bấm "Đã phản hồi khách" ở dòng là phiếu đó cũng nhận mốc phản hồi. Ngược lại, bấm nút cùng tên trên phiếu ở thẻ "Phiếu khiếu nại" thì dòng sổ cũng nhận mốc.',
          'Dòng đã lập thành phiếu thì không kết luận ở đây nữa; xử tiếp ở thẻ "Phiếu khiếu nại" ngay bên cạnh.',
          "Đổi khoảng ngày là đổi toàn bộ danh sách bên dưới, kể cả con số trên ba thẻ con.",
          "Bạn chỉ thấy tiếng khách của cơ sở thuộc phạm vi mình, nên hai người mở cùng thẻ có thể thấy số dòng khác nhau.",
          'Ghi được cả khi chưa biết khách là ai và chưa có đơn nào — đó là cái thẻ này làm được mà thẻ "Phiếu khiếu nại" không.',
          'Ô "Nơi xảy ra" chọn được một cơ sở, hoặc cả một vùng khi chỉ biết miền, hoặc "Chưa rõ nơi" khi chưa biết gì. Để trống thì máy lấy cơ sở của bạn.',
          'Tìm ra đơn lúc nào thì bấm "Gắn đơn" lúc đó; gắn xong vụ chuyển về cơ sở của đơn và mới lập phiếu khiếu nại được.',
          "Mỗi dòng gắn được đúng một đơn, và gắn rồi thì không đổi sang đơn khác.",
        ],
        statuses: [
          {
            label: "Chờ phản hồi",
            meaning: "Khách đã nói nhưng chưa ai trả lời khách. Việc gấp nhất trên thẻ.",
          },
          {
            label: "Đang xử lý",
            meaning: "Đã trả lời khách, hoặc đã có người nhận việc, nhưng chưa chốt kết quả.",
          },
          { label: "Đã xong", meaning: "Đã ghi kết quả xử lý." },
          {
            label: "Đã lập phiếu",
            meaning: "Vụ này đã chuyển sang thẻ Phiếu khiếu nại, xử tiếp ở đó.",
          },
          {
            label: "Mức Nặng",
            meaning:
              "Khách đòi tiền, nói sẽ bỏ đi, hoặc nói sẽ đăng lên mạng. Vụ này cấp quản lý mới kết luận được.",
          },
          { label: "Mức Thường / Mức Nhẹ", meaning: "Vụ xử trong ca, không cần cấp trên." },
          {
            label: "Đã hứa gọi lại trước …",
            meaning:
              "Giờ tiệm đã hứa với khách sẽ gọi lại. Phải gọi khách trước giờ này.",
          },
          {
            label: "sắp tới hẹn / đã quá hẹn",
            meaning:
              "Chưa ai phản hồi khách mà giờ hứa sắp tới, hoặc đã qua. Đã quá hẹn là tiệm đang thất hứa với khách.",
          },
          {
            label: "đã giữ hẹn / phản hồi sau hẹn",
            meaning:
              "Đã phản hồi khách trước giờ hứa, hoặc có phản hồi nhưng sau giờ hứa.",
          },
        ],
        blocks: [
          {
            for: ["cskh", "reception", "sale"],
            title: "Việc của bạn trên thẻ này",
            steps: [
              'Mở thẻ con "Chờ phản hồi" trước — đó là những khách đang đợi nghe tiếng bên mình.',
              'Dòng có chữ "Đã hứa gọi lại trước …" làm trước tiên: gọi khách trước giờ đó, vì đó là lời tiệm đã hứa.',
              "Đọc nguyên văn lời khách trên dòng, đừng đoán theo mã đơn.",
              'Gọi hoặc nhắn cho khách, rồi bấm "Đã phản hồi khách". Bấm sau khi đã nói thật, không bấm trước.',
              'Bấm "Nhận việc" để mọi người biết vụ này đang có người theo.',
              'Khách gọi điện, nói tại quầy, hay chê trên mạng mà chưa có dòng nào: bấm "Ghi tiếng khách", chọn kênh cho đúng, ghi nguyên văn lời khách.',
              'Chưa biết khách là ai cũng ghi được — chọn nơi rồi ghi, đừng đợi tra ra đơn mới ghi.',
              'Chỉ biết miền mà không biết cơ sở nào thì chọn "Cả vùng …"; đừng chọn bừa một cơ sở cho đủ ô, vì vụ sẽ tính vào cơ sở đó.',
              'Tra ra đơn rồi thì mở lại dòng đó, bấm "Gắn đơn", tìm theo tên khách hoặc số điện thoại rồi chọn đúng đơn.',
              'Xử xong: bấm "Ghi kết quả", chọn một kết quả và ghi đã làm gì cho khách.',
              'Vụ cần làm lại cho khách (chụp lại, hoàn tiền, sửa ảnh): bấm "Lập phiếu khiếu nại" rồi xử tiếp ở thẻ bên cạnh.',
            ],
            troubles: [
              'Nút "Lập phiếu khiếu nại" khoá và ghi "Phải bấm Đã phản hồi khách trước" → nói với khách rồi bấm nút đó, nút này sẽ mở.',
              'Nút "Lập phiếu khiếu nại" khoá và ghi "Chưa gắn đơn" → bấm "Gắn đơn" tìm đúng đơn trước; tra mãi không ra thì xử và ghi kết quả ngay trong sổ.',
              'Ghi dòng mới mà máy báo chỉ cấp điều hành ghi được vụ chưa rõ nơi → chọn cả vùng nếu đoán được miền, còn không thì nhờ cấp trên ghi hộ.',
              'Bấm "Gắn đơn" mà máy báo đơn không tìm thấy → đơn đó ở cơ sở ngoài phạm vi bạn; nhắn cho người ở cơ sở đó.',
              'Gắn xong thì dòng biến mất khỏi danh sách → nó chuyển về cơ sở của đơn, tìm lại ở đó.',
              'Bấm "Ghi kết quả" báo "Vụ nặng phải cấp quản lý kết luận" → nhắn quản lý trực tiếp vào kết luận.',
              "Bảng trống mà chắc là có khách phàn nàn → nới rộng khoảng ngày ở đầu thẻ.",
              'Bấm lập phiếu báo đã có phiếu rồi → mở thẻ "Phiếu khiếu nại" tìm phiếu đó, đừng lập lần nữa.',
            ],
          },
          {
            for: ["lead", "exec"],
            title: "Việc của cấp quản lý",
            steps: [
              'Xem con số trên thẻ con "Chờ phản hồi" đầu ca — đó là số khách đang đợi.',
              'Soi cột "Phản hồi sau": số tô đỏ là những vụ để khách đợi lâu.',
              'Dòng ghi "đã quá hẹn" là tiệm đã lỡ lời hứa gọi lại — hỏi ngay người trực và gọi khách.',
              'Dòng cột "Nơi" ghi "Chưa rõ nơi" chỉ cấp điều hành thấy: nhận việc, gọi khách rồi bấm "Đã phản hồi khách" như mọi dòng; tra ra đơn thì "Gắn đơn" để vụ về đúng cơ sở.',
              'Vụ ghi "Mức Nặng" chỉ bạn kết luận được — đọc lời khách rồi bấm "Ghi kết quả".',
              "Vụ cần làm lại cho khách thì lập phiếu để việc đó không bị quên.",
              "Đổi khoảng ngày để so một tuần với tuần trước, xem kênh nào khách nói nhiều nhất.",
            ],
            troubles: [
              "Nhiều dòng dừng ở “Chờ phản hồi” cả ngày → hỏi lại người trực ca xem có ai đang theo không.",
              "Một kênh bỗng nhiều dòng hẳn lên → đọc vài lời khách của kênh đó, thường là cùng một chuyện.",
            ],
          },
          {
            for: ["qc", "accounting"],
            title: "Việc của bạn trên thẻ này",
            steps: [
              "Thẻ này để ĐỌC: xem khách đang nói gì về công việc thuộc phần bạn theo.",
              "Đọc nguyên văn lời khách thay vì chỉ xem con số tổng.",
              "Thấy vụ thuộc phần mình phụ trách thì nhắn cho người đang theo vụ đó.",
            ],
            troubles: [
              "Thấy một vụ cần bạn làm gì đó nhưng không bấm được nút nào → thẻ này chỉ cho bạn đọc, nhắn thẳng cho người đang theo vụ.",
            ],
          },
        ],
      },
      {
        key: "tickets",
        label: COMPLAINTS_TAB_LABEL.tickets,
        purpose:
          "Những vụ đã thành việc phải bù cho khách, theo tới lúc chốt kết quả.",
        where: [
          'Đầu thẻ: ô "Chế độ" (theo tháng hay theo đơn — theo đơn thì gõ đủ mã đơn, ví dụ AT-2609-001), ô "Tháng", ô "Tìm phiếu" (gõ tên khách, số điện thoại hoặc mã đơn), rồi các ô lọc "Bước", "Phòng ban", "Nhóm vấn đề", "Kết quả"; danh sách có từ hai cơ sở trở lên thì thêm ô "Cơ sở".',
          'Trên điện thoại chỉ còn ô tìm và nút "Bộ lọc"; các ô lọc nằm trong hộp kéo lên.',
          'Cuối hàng lọc, canh phải: nút "+ Tạo khiếu nại" và nút "Đơn hàng".',
          'Bảng chính, mỗi dòng một phiếu: cột "Hạn xử lý", "Các bước", "Nhóm vấn đề", "Phòng ban", "Lỗi của ai", cột "Đơn" (mã đơn và tên khách — bấm mã đơn để mở trang đơn) và cột "Hành động".',
          'Ở cột "Hạn xử lý", phiếu chưa có mốc phản hồi khách có thêm một dòng đếm 30 phút và nút "Đã phản hồi khách"; khối "Hạn xử lý" trong hộp "Xem" cũng có đúng dòng và nút đó. Trên điện thoại chúng nằm ở đầu thẻ của phiếu.',
          'Nút "Xem" có ở MỌI dòng, kể cả phiếu đã đóng: mở hồ sơ đầy đủ của phiếu — thông tin đơn và ê-kíp, lời và ảnh khách gửi, khối "Hồ sơ vụ", khối "Hạn xử lý", khối "Lỗi của ai", việc đã làm trên phiếu và "Dòng thời gian".',
          'Nút "Cập nhật" có trên dòng phiếu đang mở: hộp xử lý gồm ô "Khách mong muốn", ô "Số tiền khách yêu cầu bồi hoàn", ô "Phương án xử lý", hai nút "Khách đồng ý" và "Khách chưa đồng ý", khối "Lỗi của ai", ô "Chuyển sang bước", hàng nút "Kết luận", ô "Ghi chú thêm" và "Dòng thời gian".',
          'Hàng nút "Kết luận" có bốn lối xử lý — "📷 Chụp lại", "💰 Hoàn tiền", "🖥️ Sửa lại ảnh", "🎁 Tặng makeup ảnh thẻ" — và hai nút đóng thẳng: "✅ Đã xử lý ổn", "❌ Không xử lý".',
          'Ô "Chuyển sang bước" không có bước "Kết quả": phiếu chỉ sang bước đó khi có kết luận.',
          'Phiếu đã đóng: mở bằng "Xem", nút "Mở lại" nằm ở cuối hộp.',
        ],
        howItWorks: [
          "Phiếu đi qua sáu bước: thu thập, khảo sát khách, báo lên, giải trình, xử lý, rồi kết quả — bảng cho lọc theo bước để biết phiếu đang tắc ở đâu.",
          'Bảng tự xếp việc cần làm lên trên: phiếu trễ hạn đứng đầu, rồi phiếu đang chờ kế toán, rồi phiếu còn hạn; phiếu đã đóng nằm cuối. Mở thẻ ra bảng chỉ hiện phiếu đang mở — muốn xem phiếu đã đóng thì đổi ô "Bước".',
          'Mỗi phiếu có ba mốc: phản hồi khách trong 30 phút, có phương án trong 24 giờ, đóng phiếu trong 48 giờ. Cột "Hạn xử lý" báo mốc gần nhất còn bao lâu, hoặc phiếu đã trễ mốc nào.',
          'Phiếu lập tay ở thẻ này tính là đã phản hồi khách ngay lúc lập. Phiếu máy tự mở khi khách chấm 1–2 sao, và phiếu lập từ một dòng "Tiếng khách" chưa ai phản hồi, thì chưa có mốc: cột "Hạn xử lý" hiện dòng đếm "Còn … phút để phản hồi khách" kèm nút "Đã phản hồi khách".',
          'Bấm "Đã phản hồi khách" sau khi đã nói chuyện thật với khách. Mốc chỉ ghi được MỘT LẦN và lấy giờ lúc bấm; dòng "Tiếng khách" gắn với phiếu nhận cùng mốc. Bấm rồi thì dòng đếm và nút biến mất, mốc xem lại ở khối "Hạn xử lý" của hộp "Xem".',
          'Mốc "có phương án" tính là đạt từ lần đầu ô "Phương án xử lý" được ghi và lưu.',
          'Muốn đóng phiếu phải có đủ hai thứ: kết luận ở khối "Lỗi của ai" và câu trả lời của khách. Thiếu thứ nào thì hàng nút "Kết luận" khoá, kèm dòng chữ nói còn thiếu gì.',
          '"Lỗi của ai" đi hai nấc: người xử lý phiếu gửi đề xuất, cấp quản lý chốt. Đã chốt thì đề xuất không sửa được nữa.',
          'Chọn "Khách chưa đồng ý" vẫn đóng được phiếu; dòng đó mang nhãn "Khách chưa đồng ý" để cấp quản lý xem lại.',
          'Chốt kết luận ở bước nào thì phiếu cũng tự nhảy sang bước "Kết quả" và cột "Hạn xử lý" hiện "Đã xong" — không phải đi tay qua các bước còn lại mới đóng được.',
          'Ngược lại, bước "Kết quả" không chọn tay được, ở cả hộp "Tạo khiếu nại" lẫn ô "Chuyển sang bước": phiếu chưa có kết luận thì chưa xong, dù đang đứng ở bước nào.',
          'Chọn bước "Báo GĐ" rồi bấm "Lưu" là tin báo về phiếu đi ngay tới cấp điều hành.',
          'Ở bước "Giải trình", người có tên trên đơn mở phiếu bằng nút "Xem" rồi tự viết vào ô "Giải trình của tôi". Lời giải trình chỉ người viết và người phụ trách phiếu đọc được.',
          'Chữ gõ ở ô "Ghi chú thêm" thành một dòng riêng trên "Dòng thời gian", kèm giờ và tên người viết.',
          'Ô tìm đơn trong hộp "Tạo khiếu nại" thấy đơn ở mọi khâu, kể cả đơn chưa giao khi khách phàn nàn trễ hẹn; mỗi dòng ghi mã đơn và trạng thái.',
          "Bốn lối xử lý dẫn tới bốn việc thật khác nhau: giao lại cho khâu sửa ảnh, gửi đề nghị hoàn tiền sang khâu kế toán, mở một đơn chụp bù không thu tiền của khách, hoặc mở một đơn Ảnh Thẻ có makeup không thu tiền để tặng khách.",
          "Chọn chụp lại, tặng makeup ảnh thẻ hoặc sửa lại ảnh thì phiếu tự đóng với đúng kết luận đó, nên đừng chọn thử.",
          'Ô "Số tiền khách yêu cầu bồi hoàn" ghi khách đòi bao nhiêu, để người chốt cách xử biết rõ; ghi xong không tự sinh khoản hoàn nào — tiền chỉ ra khi đề nghị hoàn tiền được kế toán chi.',
          "Câu bạn ghi khi giao việc chính là câu người nhận đọc — ghi chung chung là họ làm sai ý khách.",
          'Đề nghị hoàn tiền không tự chi và không đóng phiếu ngay: phiếu hiện "Chờ kế toán chi hoàn" và tự đóng khi kế toán chi xong. Kế toán từ chối thì phiếu quay lại để chọn cách khác, và người gửi đề nghị nhận tin báo.',
          'Phiếu đã đóng mà khách quay lại thì bấm "Xem" rồi "Mở lại" và ghi lý do. Vẫn là phiếu cũ với trọn hồ sơ cũ, không lập phiếu mới.',
          'Phiếu đã đóng không đổi thẳng sang kết luận khác được: muốn đổi thì "Mở lại", ghi lý do, rồi chọn kết luận mới. Ba lối "Chụp lại", "Hoàn tiền", "Sửa lại ảnh" cũng chỉ bấm được khi phiếu đang mở.',
          'Phiếu đóng bằng "Hoàn tiền", "Chụp lại" hoặc "Tặng makeup ảnh thẻ" thì nút "Mở lại" chỉ mở với cấp quản lý chốt được lỗi; người khác thấy nút khoá kèm dòng chữ nói lý do.',
          "Bấm vào tin báo về một phiếu là trang mở thẳng hồ sơ của phiếu đó.",
          'Hộp "Đề nghị hoàn tiền cho khách" hiện sẵn khách đã trả bao nhiêu, đã hoàn bao nhiêu, bao nhiêu đang chờ kế toán duyệt và mức "Hoàn tối đa" — nhập quá mức đó là hộp chặn ngay. Hoàn tiền chỉ đi chuyển khoản, nên hộp bắt ghi tài khoản nhận tiền của khách (ngân hàng, số tài khoản, tên chủ tài khoản); kế toán chuyển về đúng tài khoản đó.',
        ],
        statuses: [
          { label: "Thu thập", meaning: "Bước đầu: gom lời kể, ảnh và thông tin đơn của khách." },
          { label: "Khảo sát KH", meaning: "Đã gọi lại hỏi khách muốn gì, để chọn đúng lối xử." },
          { label: "Báo GĐ", meaning: "Ca nặng, đã báo lên trên trước khi quyết." },
          {
            label: "🎁 Đơn tặng makeup ảnh thẻ",
            meaning: 'Hiện ở mục "Đã xử lý trên phiếu này": đơn không thu tiền mở để tặng khách makeup và chụp lại ảnh thẻ — bấm vào để mở đơn.',
          },
          { label: "Giải trình", meaning: "Bên liên quan đang viết lại đầu đuôi sự việc." },
          {
            label: "Xử lý",
            meaning: "Đang làm phần bù cho khách: sửa lại ảnh, chụp lại, hoặc lo phần tiền.",
          },
          {
            label: "Kết quả",
            meaning:
              'Bước cuối: phiếu đã có kết luận luôn đứng ở bước này. Phiếu cũ nào đứng đây mà chưa có kết luận thì vẫn là phiếu đang mở — bấm "Cập nhật" rồi chọn kết luận để đóng.',
          },
          {
            label: "Còn … phút để phản hồi khách",
            meaning:
              'Phiếu chưa có mốc phản hồi khách, còn trong 30 phút đầu. Nói chuyện với khách xong thì bấm "Đã phản hồi khách".',
          },
          {
            label: "Trễ hạn phản hồi khách 30 phút",
            meaning:
              'Đã quá 30 phút mà phiếu chưa có mốc phản hồi. Gọi hoặc nhắn khách ngay rồi bấm "Đã phản hồi khách" — muộn vẫn phải bấm, để phiếu có giờ phản hồi thật.',
          },
          {
            label: "Còn … để có phương án",
            meaning: "Phiếu mới, đang trong hạn 24 giờ phải ghi được phương án xử lý.",
          },
          {
            label: "Còn … để đóng",
            meaning: "Đã có phương án, đang trong hạn 48 giờ phải đóng phiếu. Nhãn đổi màu khi gần tới hạn.",
          },
          {
            label: "Trễ hạn có phương án 24 giờ",
            meaning: "Đã qua một ngày mà phiếu chưa ghi phương án — ghi ngay rồi báo khách.",
          },
          {
            label: "Trễ hạn đóng 48 giờ",
            meaning: "Đã quá hạn đóng phiếu, khách đang chờ quá lâu. Việc gấp nhất trên thẻ.",
          },
          {
            label: "Chờ kế toán chi hoàn",
            meaning: "Đề nghị hoàn tiền đã gửi đi; phiếu tự đóng khi kế toán chi xong.",
          },
          {
            label: "Đã xong",
            meaning:
              'Phiếu đóng với một kết luận rõ ràng, không còn nằm trong việc đang chạy. Ghi thêm "đóng sau 48 giờ" nếu phiếu đóng trễ hạn.',
          },
          {
            label: "Chưa kết luận",
            meaning: 'Cột "Lỗi của ai" còn trống — phiếu chưa đóng được.',
          },
          {
            label: "… · chờ chốt",
            meaning: 'Đã có đề xuất "Lỗi của ai", đang chờ cấp quản lý chốt.',
          },
          {
            label: "… · đã chốt",
            meaning: 'Kết luận "Lỗi của ai" đã chốt, đề xuất không sửa được nữa.',
          },
          {
            label: "Khách chưa đồng ý",
            meaning: "Phiếu đã ghi nhận khách chưa ưng với cách xử lý — cần người xem lại.",
          },
          {
            label: "Mở lại … lần",
            meaning: "Phiếu từng đóng rồi phải mở lại vì khách quay lại.",
          },
          {
            label: "⏳ Chờ kế toán",
            meaning: "Đề nghị hoàn tiền đã gửi đi, đang chờ xử ở khâu tiền.",
          },
          {
            label: "Logo + tên tiểu đội ở chỗ ghi Sale",
            meaning:
              "Người bán thuộc một tiểu đội Phòng Kinh doanh: chỗ ghi Sale hiện logo và tên tiểu đội bằng chữ to; dòng nhỏ bên dưới ghi TĐT (tiểu đội trưởng), đội và ĐT (đội trưởng), chức nào đang trống thì không ghi. Có thể kèm tên người bán ở một dòng riêng, thụt vào dưới tên tiểu đội. Đơn lên trước 01/11/2026 ghi tiểu đội hiện tại của người bán. Người bán chưa vào tiểu đội nào thì vẫn ghi tên người bán.",
          },
        ],
        blocks: [
          {
            for: ["cskh", "reception"],
            title: "Lập và chạy một phiếu khiếu nại",
            steps: [
              'Vụ đã có trong thẻ "Tiếng khách": bấm "Lập phiếu khiếu nại" ngay ở dòng đó, phiếu tự mang theo lời khách.',
              'Vụ chưa có trong sổ: bấm "+ Tạo khiếu nại" ngay trong ngày, đừng để tới lúc khách gọi lần hai.',
              "Tìm đúng đơn theo tên khách, số điện thoại hoặc mã đơn rồi gắn phiếu vào đơn đó.",
              "Chọn phân loại đúng: chất lượng ảnh, thái độ phục vụ, trễ hẹn, sai hoặc thiếu ảnh, chuyện thanh toán, hay loại khác.",
              "Mô tả vấn đề bằng lời của khách, càng cụ thể càng dễ xử; có ảnh khách gửi thì đính kèm.",
              'Phiếu nào ở cột "Hạn xử lý" còn dòng đếm phản hồi khách thì xử trước: gọi hoặc nhắn khách, rồi bấm "Đã phản hồi khách" ngay trên dòng đó.',
              'Bấm "Cập nhật" trên dòng phiếu. Gọi lại cho khách để hiểu rõ mong muốn rồi ghi vào ô "Khách mong muốn".',
              'Khách đòi tiền thì ghi đúng số khách đòi vào ô "Số tiền khách yêu cầu bồi hoàn".',
              'Ghi cách tiệm sẽ xử vào ô "Phương án xử lý" rồi bấm "Lưu" — làm ngay trong ngày, đừng để qua hôm sau.',
              'Ở khối "Lỗi của ai": chọn lỗi của cơ sở, của một phòng, của một người, hoặc không phải lỗi tiệm; ghi ngắn vì sao rồi bấm "Gửi đề xuất".',
              'Chọn "Lỗi của một người" thì bấm tên người đó trong danh sách người có tên trên đơn, hoặc chọn ở ô "Thêm người khác". Lỗi của nhiều người thì bấm thêm tên (bấm lại một tên là bỏ người đó).',
              'Mỗi người đã chọn hiện một dòng có ảnh và ô "Lý do riêng (không bắt buộc)". Ghi lý do chung ở ô "Vì sao là lỗi đó", hoặc bỏ trống ô đó và ghi lý do riêng cho TỪNG người.',
              'Báo lại khách phương án, rồi bấm "Khách đồng ý" hoặc "Khách chưa đồng ý" đúng như khách trả lời.',
              'Chọn lối xử lý ở hàng "Kết luận": giao khâu sửa ảnh làm lại, đề nghị hoàn tiền, hoặc mở đơn chụp bù cho khách.',
              "Giao khâu sửa ảnh: ghi rõ ảnh nào, sai chỗ nào, cần sửa thành thế nào.",
              "Mở đơn chụp bù: ghi rõ chụp lại phần nào; hẹn được ngày thì chọn ngày luôn, chưa hẹn được thì để đơn ở dạng dự bị.",
              "Ô chi nhánh trong bảng chụp bù điền sẵn cơ sở của đơn cũ — khách hẹn tới cơ sở khác thì đổi ở đó trước khi lưu.",
              'Tặng khách makeup ảnh thẻ: bấm "🎁 Tặng makeup ảnh thẻ", ghi chú cho ê-kíp biết tặng những gì, chọn cơ sở và ngày giờ như khi chụp bù rồi bấm "Tạo đơn tặng".',
              'Vụ đã xử xong mà không cần ba lối trên thì chọn "✅ Đã xử lý ổn" rồi bấm "Lưu"; đừng để phiếu treo khi đã xử xong.',
              'Cần ghi lại một việc vừa làm (đã gọi khách, đã hẹn ngày…) thì gõ vào ô "Ghi chú thêm" rồi bấm "Lưu".',
            ],
            troubles: [
              'Hàng nút "Kết luận" khoá và ghi "Chưa đóng được phiếu" → làm đúng thứ dòng chữ đó nêu: gửi đề xuất ở khối "Lỗi của ai", hoặc bấm câu trả lời của khách.',
              'Khối "Lỗi của ai" ghi "Kết luận đã chốt" và không sửa được → thấy kết luận chưa đúng thì nhắn quản lý trực tiếp.',
              'Nút "Gửi đề xuất" khoá và ghi "Ghi lý do chung, hoặc thêm lý do riêng cho: …" → ghi vào ô "Vì sao là lỗi đó", hoặc ghi lý do riêng ở dòng của từng người được nêu tên.',
              'Chọn nhầm một người ở khối "Lỗi của ai" → bấm "Bỏ" ở dòng của người đó, hoặc bấm lại tên của họ.',
              'Chọn "❌ Không xử lý" mà nút "Lưu" khoá → ghi lý do vào ô "Ghi chú thêm".',
              'Nút "✅ Đã xử lý ổn" khoá, dòng chữ ghi đang chờ kế toán chi hoàn → đợi kế toán xử đề nghị hoàn tiền xong.',
              "Nhận tin báo đề nghị hoàn tiền bị từ chối → bấm tin báo để mở phiếu, đọc lý do trên dòng thời gian rồi chọn lại cách bù cho khách.",
              'Khách quay lại sau khi phiếu đã đóng → gõ tên khách vào ô "Tìm phiếu" và đổi ô "Bước" sang "Đã xong", bấm "Xem" rồi "Mở lại".',
              'Nút "Mở lại" khoá kèm dòng chữ → phiếu đã đóng bằng hoàn tiền, chụp lại hoặc tặng makeup ảnh thẻ; nhắn quản lý trực tiếp mở lại giúp, kèm lý do khách quay lại.',
              'Phiếu báo "Trễ hạn đóng 48 giờ": báo quản lý trực tiếp ngay, đồng thời gọi khách cập nhật tình hình.',
              'Bấm "Đã phản hồi khách" mà được báo phiếu đã có mốc từ trước: đồng nghiệp đã ghi mốc trước bạn, không cần làm gì thêm.',
              'Không tìm thấy đơn để gắn phiếu: kiểm lại số điện thoại hoặc mã đơn. Vẫn không ra thì bấm "Không tìm ra đơn? Ghi vào sổ Tiếng khách" ngay trong hộp để ghi lời khách trước; tra ra đơn lúc nào thì bấm "Gắn đơn" ở dòng đó rồi lập phiếu.',
              "Ô chi nhánh không có cơ sở khách muốn tới: cơ sở đó nằm ngoài phần bạn phụ trách, nhờ quản lý trực tiếp.",
              'Chọn ngày chụp lại mà ngay dưới ô hiện dòng vàng "⚠️ Ngày chụp lạ …" → ngày đó trước năm 2020 hoặc quá hai năm tới, thường là chọn nhầm năm; xem lại đúng ngày đã hẹn với khách rồi mới bấm tạo.',
              "Khách đòi hoàn tiền ngay: gửi đề nghị hoàn tiền kèm lý do rõ ràng, đừng hứa trước với khách về thời điểm chi.",
            ],
          },
          {
            for: ["retouch", "photo", "makeup", "assistant"],
            title: "Khi phiếu liên quan tới bạn",
            steps: [
              'Bấm "Xem" trên dòng phiếu để đọc lời khách và ảnh khách gửi kèm trước khi làm lại.',
              "Yêu cầu sửa ảnh sẽ về màn việc của bạn kèm đúng câu ghi trong phiếu.",
              "Làm lại xong thì trả kết quả theo đường bình thường, đừng trả thẳng cho khách.",
              'Được đề nghị giải trình: trong hộp "Xem" có ô "Giải trình của tôi" — viết lại việc đã xảy ra theo góc nhìn của bạn rồi bấm "Gửi giải trình".',
            ],
            troubles: [
              'Không thấy ô "Giải trình của tôi" → ô chỉ hiện với người có tên trên đơn đó; cần viết thì nhắn người lập phiếu.',
              "Câu yêu cầu ghi quá chung, không rõ sửa gì: hỏi lại người lập phiếu, đừng đoán rồi làm — làm sai lần hai là khách mất kiên nhẫn.",
              "Ảnh khách gửi kèm không mở được: nhắn lại xin khách gửi bản khác, ghi vào phiếu là đang chờ ảnh.",
            ],
          },
          {
            for: ["accounting"],
            title: "Đề nghị hoàn tiền gửi sang bạn",
            steps: [
              'Đề nghị hoàn tiền tới bạn ở trang Thanh toán, hàng chờ duyệt (dòng có số tiền âm). Bấm "✓ Duyệt" để mở hộp "Duyệt chi hoàn tiền": hộp ghi số tiền, lý do hoàn tiền và tài khoản nhận.',
              'Ở trang Thanh toán, hàng chờ duyệt ghi "Hoàn về: …" — tài khoản CSKH ghi cho khách. Chuyển khoản về đúng tài khoản đó, đính ảnh lệnh chuyển vào hộp rồi mới bấm "✓ Duyệt chi".',
              "Lý do trống hoặc mập mờ thì hỏi lại người lập phiếu trước khi xử.",
              "Xử xong thì trạng thái phiếu đổi theo, người chăm sóc khách sẽ báo lại khách.",
              "Từ chối một đề nghị thì ghi rõ lý do: phiếu quay lại người lập và họ đọc đúng câu bạn ghi.",
            ],
            troubles: [
              "Số tiền đề nghị không khớp với số đã thu trên đơn: dừng lại, hỏi lại người lập phiếu trước khi làm tiếp.",
              'Phiếu đứng mãi ở "⏳ Chờ kế toán": tìm đề nghị đó ở hàng chờ duyệt trang Thanh toán, bấm "✓ Duyệt" xem còn thiếu gì, rồi nhắn thẳng cho người lập phiếu bổ sung.',
            ],
          },
          {
            for: ["sale", "marketing", "qc"],
            title: "Khi bạn chỉ cần theo dõi",
            steps: [
              'Gõ tên khách, số điện thoại hoặc mã đơn vào ô "Tìm phiếu" để ra phiếu của khách mình; lọc theo bước để biết phiếu đang nằm ở khâu nào.',
              'Bấm "Xem" để đọc lời khách, phương án và kết luận — biết đã hứa gì với khách trước khi liên hệ lại. Phiếu đã đóng cũng mở xem được.',
            ],
            troubles: [
              "Khách hỏi bao giờ xong mà phiếu chưa có kết luận: hỏi người đang giữ phiếu, đừng hẹn ngày thay họ.",
            ],
          },
          {
            for: ["lead", "exec"],
            title: "Theo dõi khiếu nại",
            steps: [
              'Phiếu trễ hạn tự đứng đầu bảng — nhìn cột "Hạn xử lý" trước tiên, phiếu càng trễ càng hỏng quan hệ với khách.',
              'Dòng còn nhãn "Trễ hạn phản hồi khách 30 phút" là phiếu chưa ai ghi mốc phản hồi — hỏi người trực xem đã liên hệ khách chưa.',
              "Lọc theo bước để biết phiếu đang tắc ở khâu nào.",
              'Lỗi đã chốt là "Lỗi của một người" và kế toán đã chi hoàn cho khách: trong hộp "Cập nhật" (hoặc "Xem" với phiếu đã đóng) có khối "Bồi thường khấu trừ vào lương" ngay dưới "Lỗi của ai". Vụ quy lỗi cho nhiều người thì mỗi người một khối riêng. Điền số tiền nhân sự chịu, chọn chia trong mấy tháng, tải ảnh biên bản ghi nhận lỗi có ký và ảnh bằng chứng lỗi; nhân sự chưa viết giải trình trên phiếu thì tải ảnh bản giải trình viết tay. Bấm "Lưu nháp".',
              'Đủ bốn dòng minh chứng có dấu ✅ thì bấm "Chốt khấu trừ" — khoản trừ đi vào phiếu lương của người đó từ kỳ lương chưa chốt kế tiếp, dòng tên "Bồi thường khiếu nại".',
              'Màn của bạn có nút "Chờ chốt lỗi" thì bấm vào: đó là các phiếu có đề xuất "Lỗi của ai" đang chờ bạn. Bấm "Xem", đọc hồ sơ và lời giải trình, rồi bấm "Chốt đề xuất này" — hoặc sửa lại rồi bấm "Lưu và chốt".',
              'Chốt "Lỗi của một người" thì chọn ô "Mã lỗi" ở dòng của từng người bị ghi — danh sách chỉ có mã chung và mã nghề của chính người đó. Chốt xong, sổ lỗi có sẵn MỖI NGƯỜI một phiếu lỗi CHỜ DUYỆT theo mã của họ; người bị ghi vẫn phúc khảo được như mọi phiếu lỗi.',
              'Dòng mang nhãn "Khách chưa đồng ý" là phiếu khách chưa ưng cách xử lý — mở ra xem có cần mở lại để xử tiếp không.',
              'Lọc theo "Nhóm vấn đề" và "Cơ sở" để thấy chuyện gì lặp lại ở đâu: lặp nhiều là lỗi hệ thống chứ không phải xui.',
              'Nhận tin báo từ bước "Báo GĐ" thì bấm tin báo — trang mở thẳng hồ sơ của phiếu đó.',
            ],
            troubles: [
              "Nhiều phiếu cùng trễ hạn: xem chúng có chung một bước hay một cơ sở không — tắc một chỗ thì gỡ một chỗ là hết.",
              'Bảng trống trơn dù có khiếu nại: kiểm lại ô "Tháng", ô "Bước" và ô "Tìm phiếu", rất hay còn dính bộ lọc của lần xem trước.',
              'Không thấy nút "Chờ chốt lỗi": hiện không có đề xuất nào đang chờ, hoặc việc chốt không thuộc phần bạn.',
              'Nút chốt khoá và ghi "Chọn mã lỗi cho người bị ghi rồi mới chốt được" (hoặc "…cho từng người bị ghi…") → chọn ô "Mã lỗi" ở dòng của mỗi người còn trống. Không có mã nào khớp chuyện xảy ra thì nhắn quản lý trực tiếp trước khi chốt.',
              'Lỡ chốt nhầm người: sửa lại người đúng rồi "Lưu và chốt" — người đúng có phiếu lỗi mới; phiếu cũ vẫn nằm chờ ở sổ lỗi, nhắn quản lý trực tiếp để gạt phiếu đó đi.',
              'Bấm chốt mà màn báo chưa lập được phiếu lỗi: lỗi CHƯA được chốt, đề xuất vẫn đang chờ — đợi ít phút rồi bấm chốt lại.',
              'Khối "Lỗi của ai" ghi đã chốt nhưng "chưa có phiếu lỗi chờ duyệt": chọn ô "Mã lỗi" của từng người rồi bấm "Chốt lại để lập phiếu lỗi".',
              'Chốt lại mà màn báo phiếu lỗi đã lập với mã khác: chọn đúng mã màn nêu; muốn đổi mã thì sửa ở sổ lỗi, nơi phiếu đang chờ duyệt.',
              'Nút "Chốt khấu trừ" khoá → dòng minh chứng nào còn ⬜ thì bổ sung; dòng "Phiếu chi hoàn tiền kế toán" tự đủ khi kế toán chi xong.',
              'Không thấy khối "Bồi thường khấu trừ vào lương" → khối chỉ hiện khi "Lỗi của ai" đã chốt là lỗi của một người.',
              'Chốt nhầm số tiền hay nhầm người → ghi lý do rồi bấm "Huỷ khoản trừ"; khoản đã trừ ở tháng đã chốt lương giữ nguyên. Sau đó sửa và bấm "Lưu nháp" để lập lại.',
            ],
          },
        ],
      },
    ],
  },
};
